/**
 * utils/expenses.js
 * Biaya Lain-Lain / Pengeluaran — our OWN costs (the cost system the client confirmed,
 * 2026-10-11). Pure.
 *
 * Money we pay to someone who isn't the buyer/supplier and nobody pays us back: a driver tip,
 * a truck, bongkar muat, a broker, parking. ONE list, `data.expenses`:
 *   - LINKED to a transaction (`txnId`): typed in that transaction's own form, in its "Biaya
 *     Lain-Lain (tidak ditagih)" box, and **changed only there** (Edit Transaksi — the one
 *     editing surface, the same place the old in-transaction "biaya" rows were edited; changes
 *     show in its Riwayat Perubahan). Dated and located like its transaction
 *     (`linkedExpenseFields`). The Pengeluaran page shows it read-only, linked.
 *   - STANDALONE (`txnId: null`, e.g. parking on a quiet day): added, changed and deleted on
 *     the Pengeluaran page. An expense whose transaction was deleted since is treated the same
 *     way (`isStandaloneExpense`) — deleting a transaction keeps its costs, because the money
 *     was really spent; they then show "(transaksi sudah dihapus)".
 *   - What a linked one does: on a Penjualan/Transfer it lowers "Total Dikurangi Biaya"; on a
 *     Pembelian it is part of what the goods cost (spread into HPP by value, utils/wac.js).
 *     It never changes what anyone owes.
 *
 * Shape: { id, date: "YYYY-MM-DD", location: "PUSAT"|"CABANG", branchId: string|null,
 *          category, paidTo, amount, note, txnId: string|null, createdAt }
 * The v26 migration (storage.js) moved every old in-transaction "biaya" cost row here.
 *
 * Add/update/delete return the SAME reference on invalid input (defense in depth); callers
 * show the reason first via `validateExpense`, the same pattern as contacts.js.
 */
import { generateId } from "./idGenerators";

export const EXPENSE_CATEGORIES = [
  "Ongkir",
  "Tip / uang rokok sopir",
  "Komisi pihak luar",
  "Parkir / tol / bensin",
  "Bongkar muat",
  "Lainnya",
];

const isDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** @returns {{ok: boolean, error: string|null}} */
export function validateExpense(data, fields) {
  const f = fields || {};
  if (!isDate(f.date)) return { ok: false, error: "Tanggal wajib diisi." };
  if (f.location !== "PUSAT" && f.location !== "CABANG") return { ok: false, error: "Pilih lokasi: Pusat atau Cabang." };
  if (f.location === "CABANG" && f.branchId != null && !(data.branches || []).some((b) => b.id === f.branchId)) {
    return { ok: false, error: "Pilih cabang." };
  }
  if (f.location === "CABANG" && f.branchId == null && !f.txnId) return { ok: false, error: "Pilih cabang." };
  if (!f.category || !String(f.category).trim()) return { ok: false, error: "Pilih kategori." };
  if (typeof f.amount !== "number" || !Number.isFinite(f.amount) || f.amount <= 0) {
    return { ok: false, error: "Jumlah harus lebih dari 0." };
  }
  if (f.txnId && !(data.transactions || []).some((t) => t.id === f.txnId)) {
    return { ok: false, error: "Transaksi yang ditautkan tidak ditemukan." };
  }
  return { ok: true, error: null };
}

/** The transaction form's own row check (category + amount > 0) — what blocks Simpan. */
export function checkExpenseRows(rows) {
  for (const r of rows || []) {
    if (!r.category) return { ok: false, error: "Pilih kategori untuk setiap biaya lain-lain." };
    if (!(typeof r.amount === "number" && r.amount > 0)) return { ok: false, error: "Jumlah biaya lain-lain harus lebih dari 0." };
  }
  return { ok: true, error: null };
}

const clean = (f) => ({
  date: f.date,
  location: f.location,
  branchId: f.location === "CABANG" ? (f.branchId ?? null) : null,
  category: String(f.category).trim(),
  paidTo: (f.paidTo || "").trim(),
  amount: f.amount,
  note: (f.note || "").trim(),
});

export function addExpense(data, fields, id = generateId(), createdAt = new Date().toISOString()) {
  if (!validateExpense(data, fields).ok) return data;
  const expense = { id, ...clean(fields), txnId: fields.txnId || null, createdAt };
  return { ...data, expenses: [...(data.expenses || []), expense] };
}

/** `txnId` is fixed at creation — an expense is never moved to another transaction. */
export function updateExpense(data, id, fields) {
  const cur = (data.expenses || []).find((e) => e.id === id);
  if (!cur) return data;
  const next = { ...cur, ...clean({ ...cur, ...fields }) };
  // A cost whose transaction was deleted since stays editable (as standalone): its stale
  // txnId must not fail the "linked transaction exists" check.
  const linkAlive = next.txnId && (data.transactions || []).some((t) => t.id === next.txnId);
  if (!validateExpense(data, linkAlive ? next : { ...next, txnId: null }).ok) return data;
  return { ...data, expenses: data.expenses.map((e) => (e.id === id ? next : e)) };
}

export function deleteExpense(data, id) {
  if (!(data.expenses || []).some((e) => e.id === id)) return data;
  return { ...data, expenses: data.expenses.filter((e) => e.id !== id) };
}

export function expensesForTxn(expenses, txnId) {
  return (expenses || []).filter((e) => e.txnId === txnId);
}

export function sumExpenses(list) {
  return (list || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
}

/** Changed on the Pengeluaran page: no transaction, or its transaction no longer exists. */
export function isStandaloneExpense(data, e) {
  return !e.txnId || !(data.transactions || []).some((t) => t.id === e.txnId);
}

/** Where a cost linked to `txn` is booked: the transaction's own date and location. */
export function linkedExpenseFields(txn) {
  return {
    date: txn.date,
    location: txn.location === "CABANG" ? "CABANG" : "PUSAT",
    branchId: txn.location === "CABANG" ? txn.branchId || null : null,
    txnId: txn.id,
  };
}

/** "Tip / uang rokok sopir — Pak Budi (antar barang)" — how a cost reads in an edit log / list. */
export function expenseLabel(e) {
  return `${e.category}${e.paidTo ? ` — ${e.paidTo}` : ""}${e.note ? ` (${e.note})` : ""}`;
}

/**
 * Make `txn`'s linked costs exactly `rows` (the transaction form's "Biaya Lain-Lain" box):
 * a row with an `id` updates that linked cost, a row without one is added, and a linked cost
 * missing from `rows` is removed. Every one is (re)stamped with the transaction's own date and
 * location. `txn` must already be in `data.transactions`.
 * @returns {{ ok: boolean, error: string|null, data }} — `data` unchanged when not ok.
 */
export function syncLinkedExpenses(data, txn, rows) {
  const check = checkExpenseRows(rows);
  if (!check.ok) return { ...check, data };
  const linked = expensesForTxn(data.expenses, txn.id);
  const linkedIds = new Set(linked.map((e) => e.id));
  for (const r of rows || []) {
    if (r.id && !linkedIds.has(r.id)) return { ok: false, error: "Biaya lain-lain tidak dikenal.", data };
  }
  const keep = new Set((rows || []).filter((r) => r.id).map((r) => r.id));
  let next = { ...data, expenses: (data.expenses || []).filter((e) => e.txnId !== txn.id || keep.has(e.id)) };
  for (const r of rows || []) {
    const fields = { category: r.category, paidTo: r.paidTo, amount: r.amount, note: r.note, ...linkedExpenseFields(txn) };
    const before = next;
    next = r.id ? updateExpense(next, r.id, fields) : addExpense(next, fields);
    // add/update always return a NEW object on success, so the same reference means refused.
    if (next === before) return { ok: false, error: "Biaya lain-lain tidak valid.", data };
  }
  return { ok: true, error: null, data: next };
}
